/** Stage 3: what one row is, and which rows to keep. */

import { useState } from "react";

import {
  Checkbox,
  ConditionList,
  Field,
  FieldRow,
  SampleRows,
  Segmented,
  Select,
  SkeletonRows,
} from "@/components/kit";
import type { Suggestion } from "@/components/kit";
import type { Preview, Sample } from "@/lib/integrations/api";
import { listColumns, showValue, type Settings } from "@/lib/integrations/settings";

export function columnSuggestions(sample: Sample | undefined): Suggestion[] {
  return (sample?.columns ?? []).map((column) => ({
    value: column.path,
    note: column.samples.map((one) => showValue(one, 24)).join(" · "),
  }));
}

export function RowsStage({
  settings,
  onChange,
  sample,
  preview,
  previewBusy,
  disabled = false,
}: {
  settings: Settings;
  onChange: (settings: Settings) => void;
  sample: Sample | undefined;
  preview: Preview | undefined;
  previewBusy: boolean;
  disabled?: boolean;
}) {
  const [index, setIndex] = useState(0);
  const lists = listColumns(sample?.columns ?? []);
  const counts = preview?.counts ?? {};
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
  const skipped = counts.skipped ?? 0;
  const kept = (preview?.rows ?? []).filter((row) => row.outcome !== "skipped");
  const shown = Math.min(index, Math.max(0, kept.length - 1));
  const row = kept[shown];
  const raw = !settings.rows.listPath && row ? (sample?.items ?? [])[row.position] : undefined;

  return (
    <div style={{ display: "grid", gap: "var(--s5)" }}>
      <div style={{ display: "grid", gap: "var(--s3)" }}>
        <strong>What one row is</strong>
        <Segmented<"item" | "list">
          name="What one row is"
          value={settings.rows.listPath ? "list" : "item"}
          options={[
            { value: "item", label: "Each item Scout wrote" },
            { value: "list", label: "Each entry of a list inside an item" },
          ]}
          onChange={(value) =>
            onChange({
              ...settings,
              rows: { ...settings.rows, listPath: value === "item" ? null : (lists[0] ?? "") },
            })
          }
        />
        {settings.rows.listPath !== null && (
          <FieldRow>
            <Field label="Which list">
              <Select
                value={settings.rows.listPath}
                disabled={disabled}
                onChange={(event) => onChange({ ...settings, rows: { ...settings.rows, listPath: event.target.value } })}
              >
                {lists.length === 0 && <option value="">Scout sent no lists in this run</option>}
                {lists.map((path) => (
                  <option key={path} value={path}>
                    {path}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label=" ">
              <Checkbox
                label="Keep the item's own columns, as parent.*"
                checked={settings.rows.keepParent}
                disabled={disabled}
                onChange={(event) => onChange({ ...settings, rows: { ...settings.rows, keepParent: event.target.checked } })}
              />
            </Field>
          </FieldRow>
        )}
      </div>

      <div style={{ display: "grid", gap: "var(--s3)" }}>
        <strong>Which rows to keep</strong>
        <ConditionList
          keep={settings.keep}
          columns={columnSuggestions(sample)}
          disabled={disabled}
          onChange={(keep) => onChange({ ...settings, keep })}
        />
      </div>

      <div style={{ display: "grid", gap: "var(--s3)" }}>
        <strong>
          On the real run {previewBusy && <span style={{ fontWeight: 400, color: "var(--ink-3)" }}>· reading…</span>}
        </strong>
        {!preview ? (
          <SkeletonRows rows={3} columns={2} label="Reading the run" />
        ) : total === 0 ? (
          <p style={{ margin: 0, color: "var(--warn)" }}>This run has no rows. Pick another run or check the job.</p>
        ) : (
          <>
            <p style={{ margin: 0 }}>
              {total.toLocaleString()} rows · <strong>{(total - skipped).toLocaleString()} kept</strong> ·{" "}
              {skipped.toLocaleString()} left out by the keep rules
              {preview.rowsCollected > (preview.rows ?? []).length && (
                <span style={{ color: "var(--ink-3)" }}>
                  {" "}
                  · showing the first {(preview.rows ?? []).length.toLocaleString()} of {preview.rowsCollected.toLocaleString()} in previews
                </span>
              )}
            </p>
            {row && (
              <SampleRows
                theirs={raw ?? "(open Fields to see this row mapped)"}
                ours={row.value}
                index={shown}
                total={kept.length}
                onIndex={setIndex}
                note={settings.rows.listPath ? "Rows from a list show their mapped values only." : undefined}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
